import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ApiError } from '@shared/api/client';
import { resolveErrorCopy } from '@shared/errors/catalog';
import { formatDate, toMachineDate } from '@shared/format';
import { Dialog, isCancelledSubmit } from '@shared/ui/dialog';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { PlusIcon } from '@shared/ui/icons';
import { StateBadge } from '@shared/ui/StateBadge';
import { useToast } from '@shared/ui/toast/ToastProvider';

import { DetailSection, SectionAction, SurfaceError } from '../_shared/components';
import { sharedCopy } from '../_shared/copy';
import type { UserEntitlement, UserId } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { grantFormSchema, type GrantFormInput, type GrantFormValues } from './forms.schema';
import { useGrantEntitlement } from './mutations';
import { userEntitlementsKey, useUserEntitlements } from './queries';

const TABLE_COLUMNS = 6;

/**
 * §4 الاستحقاقات — every route that opens the catalogue for this account.
 *
 * **Two sources, and the two absences are stated rather than left to be
 * noticed.** The table renders subscription and manual grant because those are
 * the two the platform permits; a one-off purchase row and a free row are absent,
 * and the context line says what the two present ones do instead of drawing a
 * zero row that would advertise an access route the product does not have.
 *
 * **No amount column, and that is a surface boundary rather than an omission.**
 * Who bought, which plan, how much, and when is the subscription-reporting
 * surface's, on its own route. Repeating the figure here would put one number on
 * two surfaces that drift the first time either changes. The plan *name* is
 * shown because the entitlement is an instance of it.
 *
 * **The state is read, never derived.** Whether an entitlement is live is the
 * paywall's answer, and comparing `ends_at` against the browser's clock would
 * put that answer on the reader's machine. A row that arrives without one says
 * so rather than being told it expired.
 */
export function EntitlementsSection({ userId, userName }: { userId: UserId; userName: string }) {
  const query = useUserEntitlements(userId);
  const queryClient = useQueryClient();
  const toast = useToast();
  const grant = useGrantEntitlement(userId);

  const [open, setOpen] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const rows = query.data?.items ?? [];

  function submit(values: GrantFormValues) {
    setFormError(null);

    grant.mutate(values.endsAt, {
      onSuccess: () => {
        setOpen(false);
        toast.show(copy.entitlements.granted, 'success');
      },
      onError: (error) => {
        const status = error instanceof ApiError ? error.status : 0;
        const code = error instanceof ApiError ? error.code : undefined;
        setFormError(resolveErrorCopy(code, status, errors).message);
      },
    });
  }

  return (
    <>
      <DetailSection
        id="user-entitlements"
        heading={copy.entitlements.heading}
        context={copy.entitlements.context}
        action={
          <SectionAction onClick={() => setOpen(true)} icon={<PlusIcon />}>
            {copy.entitlements.grant}
          </SectionAction>
        }
      >
        {query.isError ? (
          <SurfaceError
            error={query.error}
            catalog={errors}
            onRetry={() => void queryClient.invalidateQueries({ queryKey: userEntitlementsKey(userId) })}
          />
        ) : null}

        {query.isPending ? (
          <>
            <p role="status" className="sr-only">
              {copy.states.loading}
            </p>
            <EntitlementTable>
              <RowSkeleton columns={TABLE_COLUMNS} rows={2} />
            </EntitlementTable>
          </>
        ) : null}

        {query.data ? (
          rows.length === 0 ? (
            <EmptyState message={copy.entitlements.empty} />
          ) : (
            <>
              <EntitlementTable>
                {rows.map((row) => (
                  <EntitlementRow key={row.id} row={row} />
                ))}
              </EntitlementTable>

              <p className="mb-0 mt-3 max-w-measure text-xs text-ink-muted">{copy.entitlements.outro}</p>
            </>
          )
        ) : null}
      </DetailSection>

      <GrantDialog
        open={open}
        userName={userName}
        busy={grant.isPending}
        formError={formError}
        onSubmit={submit}
        onCancel={() => {
          setFormError(null);
          setOpen(false);
        }}
      />
    </>
  );
}

/**
 * One entitlement.
 *
 * Every `<time>` carries an ASCII ISO-8601 `datetime` beside an Arabic-Indic
 * human string on the **same element** — the two layers never convert into each
 * other, and every sort or comparison anywhere reads the machine value.
 *
 * A manual grant's plan cell is a written absence rather than an em dash alone:
 * a bare dash is a glyph a screen reader announces as nothing, and the row still
 * has to say why the cell is empty.
 */
function EntitlementRow({ row }: { row: UserEntitlement }) {
  const source = copy.entitlements.sources[row.source] ?? row.source;
  const scope = copy.entitlements.scopes[row.scope] ?? row.scope;
  const state = row.state ? (copy.entitlements.states[row.state] ?? row.state) : null;

  return (
    <tr>
      <DTableRowHeader>{source}</DTableRowHeader>

      <td>{scope}</td>

      {/*
        An em dash, because a table column keeps its cells aligned — the
        design system's «an empty field is removed» governs a metadata list,
        not a column. The dash is `aria-hidden` and carries a visually-hidden
        sentence beside it: a bare glyph announces as nothing, and the row still
        has to say why the cell is empty.
      */}
      <td>
        {row.plan_name ?? (
          <>
            <span aria-hidden="true" className="text-ink-muted">
              —
            </span>
            <span className="sr-only">{copy.entitlements.noPlan}</span>
          </>
        )}
      </td>

      <td className="whitespace-nowrap">
        <time dateTime={toMachineDate(row.starts_at)}>
          <bdi>{formatDate(row.starts_at)}</bdi>
        </time>
      </td>

      <td className="whitespace-nowrap">
        {row.ends_at ? (
          <time dateTime={toMachineDate(row.ends_at)}>
            <bdi>{formatDate(row.ends_at)}</bdi>
          </time>
        ) : (
          <span className="text-xs text-ink-muted">{copy.entitlements.noEnd}</span>
        )}
      </td>

      <td>
        {state ? (
          <StateBadge kind={row.state === 'ACTIVE' ? 'success' : 'neutral'}>{state}</StateBadge>
        ) : (
          <span className="text-xs text-ink-muted">{copy.entitlements.stateUnknown}</span>
        )}
      </td>
    </tr>
  );
}

function EntitlementTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.entitlements.caption} minInlineSize="46rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.entitlements.columns.source}</th>
            <th scope="col">{copy.entitlements.columns.scope}</th>
            <th scope="col">{copy.entitlements.columns.plan}</th>
            <th scope="col">{copy.entitlements.columns.starts}</th>
            <th scope="col">{copy.entitlements.columns.ends}</th>
            <th scope="col">{copy.entitlements.columns.state}</th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}

/**
 * The manual grant — **a value-capturing dialog, not a confirmation.**
 *
 * It asks for a date rather than for consent, so it carries a read-back line
 * stating what will happen and when, and its commit is the primary action of its
 * own view. A second confirming dialog on top of it would train the operator to
 * dismiss dialogs, which is exactly what makes the removal dialog worthless when
 * it matters.
 *
 * **The scope is a sentence, not a field.** It is a platform setting, and the
 * two narrower values would need a subject picker this layer has no catalogue to
 * fill.
 */
function GrantDialog({
  open,
  userName,
  busy,
  formError,
  onSubmit,
  onCancel,
}: {
  open: boolean;
  userName: string;
  busy: boolean;
  formError: string | null;
  onSubmit: (values: GrantFormValues) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const fieldId = useId();
  const hintId = useId();
  const errorId = useId();

  const form = useForm<GrantFormInput, unknown, GrantFormValues>({
    resolver: zodResolver(grantFormSchema),
    defaultValues: { endsAt: '' },
  });

  const { register, handleSubmit, reset, formState, control } = form;
  const endsAt = useWatch({ control, name: 'endsAt' });

  useEffect(() => {
    if (!open) return;
    reset({ endsAt: '' });
  }, [open, reset]);

  const fieldError = formState.errors.endsAt?.message;
  const describedBy = [hintId, fieldError ? errorId : null].filter(Boolean).join(' ');

  const readBackDate =
    typeof endsAt === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(endsAt) ? `${endsAt}T00:00:00Z` : null;

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'grant') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          // «ألغِ» is a submit too — see `isCancelledSubmit`. Left alone, it lets
          // `method="dialog"` close the element instead of granting on the way out.
          if (isCancelledSubmit(event, 'grant')) return;
          event.preventDefault();
          void handleSubmit(onSubmit)(event);
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.entitlements.dialog.title(userName)}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">
            {copy.entitlements.dialog.requiredNote}
          </p>
        </div>

        {formError ? (
          <p role="alert" className="mb-0 max-w-none text-sm text-danger">
            {formError}
          </p>
        ) : null}

        <p className="mb-0 max-w-none text-xs text-ink-muted">
          {copy.entitlements.dialog.scopeLead}{' '}
          <b className="font-semibold text-ink">{copy.entitlements.dialog.scopeStrong}</b>{' '}
          {copy.entitlements.dialog.scopeRest}
        </p>

        <div className="grid gap-2">
          <label htmlFor={fieldId} className="text-sm font-medium text-ink">
            {copy.entitlements.dialog.expiresLabel}
            <span aria-hidden="true" className="ms-px text-danger">
              *
            </span>
          </label>

          {/*
            `dir="ltr"` and the mono face: a date the browser produced is a
            machine string, and an identifier transcribed into another digit set
            is a wrong identifier. The Arabic-Indic rendering happens in the
            read-back line and in the table after the save.
          */}
          <input
            id={fieldId}
            type="date"
            dir="ltr"
            aria-describedby={describedBy}
            aria-invalid={Boolean(fieldError)}
            aria-required
            className="mono hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
            {...register('endsAt')}
          />

          <p id={hintId} className="mb-0 max-w-none text-xs text-ink-muted">
            {copy.entitlements.dialog.expiresHint}
          </p>

          {fieldError ? (
            <p id={errorId} className="mb-0 max-w-none text-xs text-danger">
              {fieldError}
            </p>
          ) : null}
        </div>

        {/* The read-back: what will happen, and when, in the reader's digits. */}
        {readBackDate ? (
          <p className="mb-0 max-w-none text-sm text-ink">
            {copy.entitlements.dialog.readBackLead}{' '}
            <time dateTime={toMachineDate(readBackDate)}>
              <bdi>{formatDate(readBackDate)}</bdi>
            </time>
            .
          </p>
        ) : null}

        <div className="flex flex-wrap gap-2">
          <button
            type="submit"
            value="cancel"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {sharedCopy.action.cancel}
          </button>

          <button
            type="submit"
            value="grant"
            disabled={busy}
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-45"
          >
            {copy.entitlements.dialog.grant}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
